import { createAction, handleActions } from "redux-actions";
import { axios } from "@cores/axiosWrapper";
import axios2 from "axios";
import upload from "@cores/cpmUploadService";
import uniqId from "uniqid";
import { updateRequestStatusAPI } from "@modules/request";

function getRequestsAPI(stageId, apiEndpoint, projectId, params) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }
    return axios.get(`${apiEndpoint}/requests`, {
        params,
        headers,
    });
}

function getMyRequestsAPI(stageId, apiEndpoint, projectId, params) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }

    return axios.get(`${apiEndpoint}/my-requests`, {
        params,
        headers,
    });
}

function createRequestAPI(stageId, apiEndpoint, projectId, data) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }

    return axios.post(`${apiEndpoint}/requests`, data, {
        headers,
    });
}

function createMyRequestAttachmentsAPI(stageId, apiEndpoint, projectId, requestId, data) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }

    return axios.post(`${apiEndpoint}/my-requests/${requestId}/attachments`, data, {
        headers,
    });
}

function createRequestAttachmentsAPI(stageId, apiEndpoint, projectId, requestId, data) {
    const headers = {
        stageId,
    };

    if (projectId) {
        headers.projectId = projectId;
    }

    return axios.post(`${apiEndpoint}/requests/${requestId}/attachments`, data, {
        headers,
    });
}

const initialState = {
    data: null,
    editPending: false,
    pending: false,
    createPending: false,
    error: null,
    editError: null,
    createError: null,
};

const GET_REQUESTS_PENDING = "GET_REQUESTS_PENDING";
const GET_REQUESTS_SUCCESS = "GET_REQUESTS_SUCCESS";
const GET_REQUESTS_FAILURE = "GET_REQUESTS_FAILURE";
const UPDATE_REQUESTS_PENDING = "UPDATE_REQUESTS_PENDING";
const UPDATE_REQUESTS_SUCCESS = "UPDATE_REQUESTS_SUCCESS";
const UPDATE_REQUESTS_FAILURE = "UPDATE_REQUESTS_FAILURE";
const CREATE_REQUEST_PENDING = "CREATE_REQUEST_PENDING";
const CREATE_REQUEST_SUCCESS = "CREATE_REQUEST_SUCCESS";
const CREATE_REQUEST_FAILURE = "CREATE_REQUEST_FAILURE";
const RESET_REQUESTS = "RESET_REQUESTS";

export default handleActions(
    {
        [GET_REQUESTS_PENDING]: (state) => {
            return { ...state, pending: true, error: null };
        },
        [GET_REQUESTS_SUCCESS]: (state, action) => {
            return { ...state, data: action.payload, pending: false };
        },
        [GET_REQUESTS_FAILURE]: (state, action) => {
            return { ...state, pending: false, error: action.payload };
        },
        [UPDATE_REQUESTS_PENDING]: (state) => {
            return { ...state, editPending: true, editError: null };
        },
        [UPDATE_REQUESTS_SUCCESS]: (state, action) => {
            const { requestId, status } = action.payload;
            const results = state.data.results?.map((request) =>
                request.id === requestId ? { ...request, status } : request,
            );
            return { ...state, data: { ...state.data, results }, editPending: false };
        },
        [UPDATE_REQUESTS_FAILURE]: (state, action) => {
            return { ...state, editPending: false, editError: action.payload };
        },
        [CREATE_REQUEST_PENDING]: (state) => {
            return { ...state, createPending: true, createError: null };
        },
        [CREATE_REQUEST_SUCCESS]: (state) => {
            return { ...state, createPending: false, createError: null };
        },
        [CREATE_REQUEST_FAILURE]: (state, action) => {
            return { ...state, createPending: false, createError: action.payload };
        },
        [RESET_REQUESTS]: (state) => {
            return { ...initialState };
        },
    },
    initialState,
);

export const resetRequests = createAction(RESET_REQUESTS);

export const getRequests =
    ({ projectId, params }) =>
    (dispatch, getState) => {
        let { stage, requests } = getState();

        dispatch({ type: GET_REQUESTS_PENDING });
        return getRequestsAPI(stage.id, stage.endpoint, projectId, params)
            .then(async (response) => {
                const data = response.data;
                if (!requests.data) {
                    dispatch({
                        type: GET_REQUESTS_SUCCESS,
                        payload: data,
                    });
                } else {
                    dispatch({
                        type: GET_REQUESTS_SUCCESS,
                        payload: { ...data, results: requests.data.results.concat(data.results) },
                    });
                }
            })
            .catch((error) => {
                dispatch({
                    type: GET_REQUESTS_FAILURE,
                    payload: error.response,
                });
            });
    };

export const getMyRequests =
    ({ projectId, params }) =>
    (dispatch, getState) => {
        let { stage, requests } = getState();

        dispatch({ type: GET_REQUESTS_PENDING });
        return getMyRequestsAPI(stage.id, stage.endpoint, projectId, params)
            .then(async (response) => {
                const data = response.data;
                if (!requests.data) {
                    dispatch({
                        type: GET_REQUESTS_SUCCESS,
                        payload: data,
                    });
                } else {
                    dispatch({
                        type: GET_REQUESTS_SUCCESS,
                        payload: { ...data, results: requests.data.results.concat(data.results) },
                    });
                }
            })
            .catch((error) => {
                dispatch({
                    type: GET_REQUESTS_FAILURE,
                    payload: error.response,
                });
            });
    };

export const createRequest = (params) => async (dispatch, getState) => {
    const { stage, project } = getState();
    try {
        dispatch({ type: CREATE_REQUEST_PENDING });
        const { files, ...rest } = params;
        const response = await createRequestAPI(stage.id, stage.endpoint, project.id, rest);
        const requestId = response.data?.id;
        if (!requestId) {
            throw new Error("Failed to create request");
        }
        if (!files || files.length === 0) {
            dispatch({ type: CREATE_REQUEST_SUCCESS });
            return requestId;
        }
        let attachments = [];
        await Promise.all(
            files.map(async ({ file, title, type }) => {
                const { stage } = getState();
                const cancelTokenSource = axios._cancelToken.source();
                const uploadResponse = await new Promise((resolve, reject) => {
                    const presignUrlParams = {
                        resourceType: "REQUEST_ATTACHMENT",
                        fileName: title,
                        requestId,
                        contentType: type,
                    };
                    upload(
                        file,
                        {
                            stageVersion: stage.version,
                            apiEndpoint: stage.endpoint,
                            uploadParams: {
                                projectId: project.id,
                                ...presignUrlParams,
                            },
                            progressKey: uniqId(),
                            type: "REQUEST_ATTACHMENT",
                        },
                        cancelTokenSource.token,
                        () => {},
                        (data) => {
                            const result = {
                                url: data.destinationUrl,
                                status: "COMPLETE",
                            };
                            resolve(result);
                        },
                        () => {
                            const result = {
                                status: "CANCELED",
                            };
                            reject(result);
                        },
                        (error) => {
                            const result = {
                                status: "ERROR",
                            };
                            reject(result);
                        },
                        dispatch,
                    );
                });
                const uploadedUrl = uploadResponse.url;
                attachments.push({ url: uploadedUrl, fileName: file.name });
            }),
        );
        await createRequestAttachmentsAPI(stage.id, stage.endpoint, project.id, requestId, {
            attachments,
        });
        dispatch({ type: CREATE_REQUEST_SUCCESS });
        return requestId;
    } catch (error) {
        dispatch({ type: CREATE_REQUEST_FAILURE, payload: error });
        throw error;
    }
};

export const createMyRequest = (params) => async (dispatch, getState) => {
    const { stage, project } = getState();
    try {
        dispatch({ type: CREATE_REQUEST_PENDING });
        const { files, ...rest } = params;
        const response = await createRequestAPI(stage.id, stage.endpoint, project.id, rest);
        const requestId = response.data?.id;
        if (!requestId) {
            throw new Error("Failed to create request");
        }
        if (!files || files.length === 0) {
            dispatch({ type: CREATE_REQUEST_SUCCESS });
            return requestId;
        }
        let attachments = [];
        await Promise.all(
            files.map(async ({ file, title, type }) => {
                const { stage } = getState();
                const cancelTokenSource = axios._cancelToken.source();
                const uploadResponse = await new Promise((resolve, reject) => {
                    const presignUrlParams = {
                        resourceType: "REQUEST_ATTACHMENT",
                        fileName: title,
                        requestId,
                        contentType: type,
                    };
                    upload(
                        file,
                        {
                            stageVersion: stage.version,
                            apiEndpoint: stage.endpoint,
                            uploadParams: {
                                projectId: project.id,
                                ...presignUrlParams,
                            },
                            progressKey: uniqId(),
                            type: "REQUEST_ATTACHMENT",
                        },
                        cancelTokenSource.token,
                        () => {},
                        (data) => {
                            const result = {
                                url: data.destinationUrl,
                                status: "COMPLETE",
                            };
                            resolve(result);
                        },
                        () => {
                            const result = {
                                status: "CANCELED",
                            };
                            reject(result);
                        },
                        (error) => {
                            const result = {
                                status: "ERROR",
                            };
                            reject(result);
                        },
                        dispatch,
                    );
                });
                const uploadedUrl = uploadResponse.url;
                attachments.push({ url: uploadedUrl, fileName: file.name });
            }),
        );
        await createMyRequestAttachmentsAPI(stage.id, stage.endpoint, project.id, requestId, {
            attachments,
        });
        dispatch({ type: CREATE_REQUEST_SUCCESS });
        return requestId;
    } catch (error) {
        dispatch({ type: CREATE_REQUEST_FAILURE, payload: error });
        throw error;
    }
};

export const updateRequestStatus =
    ({ projectId, requestId, params }) =>
    (dispatch, getState) => {
        let { stage } = getState();

        dispatch({ type: UPDATE_REQUESTS_PENDING });
        return new Promise((resolve, reject) => {
            updateRequestStatusAPI(stage.id, stage.endpoint, projectId, requestId, params)
                .then(async (response) => {
                    dispatch({
                        type: UPDATE_REQUESTS_SUCCESS,
                        payload: { requestId, status: params.value },
                    });
                    resolve(response);
                })
                .catch((error) => {
                    dispatch({ type: UPDATE_REQUESTS_FAILURE, payload: error });
                    reject(error);
                });
        });
    };
